import { useCallback, useRef, useState } from "react";

type Identifiable = {
  id: string;
};

type LoadingQueueItem<T extends Identifiable> = {
  item: T;
  retries?: number;
};

type LoadingQueueProps<T extends Identifiable> = {
  processItem: (item: T) => Promise<void>;
};

const MAX_RETRIES = 3;

const useLoadingQueue = <T extends Identifiable>({
  processItem,
}: LoadingQueueProps<T>) => {
  const queueRef = useRef<LoadingQueueItem<T>[]>([]); // Intentional, mutable value that doesn't trigger rerenders on change.
  const [isProcessing, setIsProcessing] = useState(false);

  // Dequeue and process items
  const processQueue = useCallback(async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    while (queueRef.current.length > 0) {
      const currentItem = queueRef.current.shift();

      if (!currentItem) {
        console.error("Failed to dequeue item:", currentItem);
        continue;
      }

      // This is just a simple backoff attempt when we encounter errors.
      if (currentItem.retries && currentItem.retries > 0) {
        const delay = Math.pow(2, currentItem.retries) * 1000; // Exponential backoff
        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      try {
        await processItem(currentItem.item);
      } catch (error) {
        console.error(`Failed to process item ${currentItem.item.id}:`, error);

        const retries = currentItem.retries ?? 0;
        if (retries < MAX_RETRIES) {
          queueRef.current.unshift({ ...currentItem, retries: retries + 1 }); // Add to the front of the queue
        } else {
          console.error(`Max retries reached for item ${currentItem.item.id}`);
        }
      }
    }

    setIsProcessing(false);
  }, [isProcessing, processItem]);

  // Enqueue a new conversation
  const enqueue = (item: T) => {
    queueRef.current.push({
      item,
      retries: 0,
    });
    processQueue(); // Trigger processing if not already processing
  };

  return {
    enqueue,
  };
};

export default useLoadingQueue;
